import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import { forEachBitmapImage } from './slideBitmapImageBlocks';
import {
  deleteLearningWorksheetImageByPublicUrl,
  isLearningWorksheetImageStoragePublicUrl,
  uploadLearningWorksheetImage,
} from '../services/learningWorksheetImageStorageService';

type PendingEntry = {
  blob: Blob;
  mimeType: string;
  objectUrl: string;
  blockId: string;
};

const pendingByObjectUrl = new Map<string, PendingEntry>();
const pendingByBlockId = new Map<string, PendingEntry>();

export function isPendingLearningWorksheetImageSrc(src: string): boolean {
  return src.trim().startsWith('blob:');
}

export function registerPendingLearningWorksheetImage(
  blockId: string,
  blob: Blob,
  mimeType: string
): string {
  revokePendingLearningWorksheetForBlock(blockId);
  const objectUrl = URL.createObjectURL(blob);
  const entry: PendingEntry = { blob, mimeType, objectUrl, blockId };
  pendingByObjectUrl.set(objectUrl, entry);
  pendingByBlockId.set(blockId, entry);
  return objectUrl;
}

export function registerPendingLearningWorksheetClipboardImage(
  blockId: string,
  blob: Blob,
  mimeType: string
): string {
  return registerPendingLearningWorksheetImage(blockId, blob, mimeType);
}

export function clonePendingLearningWorksheetImageForBlock(src: string, newBlockId: string): string | null {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) return null;
  const entry = pendingByObjectUrl.get(trimmed);
  if (!entry) return null;
  return registerPendingLearningWorksheetImage(newBlockId, entry.blob, entry.mimeType);
}

export function revokePendingLearningWorksheetImageSrc(src: string): void {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) return;
  const entry = pendingByObjectUrl.get(trimmed);
  if (!entry) return;
  URL.revokeObjectURL(entry.objectUrl);
  pendingByObjectUrl.delete(trimmed);
  pendingByBlockId.delete(entry.blockId);
}

export function revokePendingLearningWorksheetForBlock(blockId: string): void {
  const entry = pendingByBlockId.get(blockId);
  if (entry) revokePendingLearningWorksheetImageSrc(entry.objectUrl);
}

async function finalizeBitmapImagesArray(
  images: ProgramSlideLayoutV1['clipboardImages'],
  workspaceId: string
): Promise<{ images: ProgramSlideLayoutV1['clipboardImages']; changed: boolean }> {
  if (!images.length) return { images, changed: false };

  let changed = false;
  const next = await Promise.all(
    images.map(async (img) => {
      const src = img.src?.trim() ?? '';
      if (!src || !isPendingLearningWorksheetImageSrc(src)) return img;
      const entry = pendingByObjectUrl.get(src);
      if (!entry) {
        changed = true;
        return { ...img, src: '' };
      }
      const { publicUrl } = await uploadLearningWorksheetImage({
        blob: entry.blob,
        mimeType: entry.mimeType,
        workspaceId,
      });
      revokePendingLearningWorksheetImageSrc(src);
      changed = true;
      return { ...img, src: publicUrl };
    })
  );

  return { images: next, changed };
}

export async function finalizePendingLearningWorksheetImagesInLayout(params: {
  layout: ProgramSlideLayoutV1;
  workspaceId: string;
  previousLayout?: ProgramSlideLayoutV1 | null;
}): Promise<ProgramSlideLayoutV1> {
  const { layout, workspaceId, previousLayout } = params;
  const [clipboardResult, scanResult] = await Promise.all([
    finalizeBitmapImagesArray(layout.clipboardImages ?? [], workspaceId),
    finalizeBitmapImagesArray(layout.scanImages ?? [], workspaceId),
  ]);

  if (previousLayout) {
    await releaseRemovedLearningWorksheetImageUrls(previousLayout, layout);
  }

  if (!clipboardResult.changed && !scanResult.changed) return layout;

  return {
    ...layout,
    clipboardImages: clipboardResult.images,
    scanImages: scanResult.images,
  };
}

export async function releaseRemovedLearningWorksheetImageUrls(
  previousLayout: ProgramSlideLayoutV1,
  nextLayout: ProgramSlideLayoutV1
): Promise<void> {
  const prevUrls = new Set<string>();
  const nextUrls = new Set<string>();

  forEachBitmapImage(previousLayout, (_kind, img) => {
    const src = img.src?.trim();
    if (src && isLearningWorksheetImageStoragePublicUrl(src)) prevUrls.add(src);
  });
  forEachBitmapImage(nextLayout, (_kind, img) => {
    const src = img.src?.trim();
    if (src && isLearningWorksheetImageStoragePublicUrl(src)) nextUrls.add(src);
  });

  const removed = Array.from(prevUrls).filter((url) => !nextUrls.has(url));
  await Promise.all(removed.map((url) => deleteLearningWorksheetImageByPublicUrl(url)));
}
